import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type CapModuleSpecsFragment } from '../../../../api/fragments/CapModuleSpecs';
import {
    UpdateCapModuleDocument,
    type UpdateCapModuleMutation,
    type UpdateCapModuleMutationVariables,
} from '../../../../api/mutations/updateCapModule';
import { type CapModuleSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type CapModuleUpdateFormProps = {
    module: CapModuleSpecsFragment;
    onComplete: () => void;
};

type FormValues = CapModuleSettings;

const CapModuleUpdateForm = ({ module, onComplete }: CapModuleUpdateFormProps) => {
    const { t } = useTranslation(['capModuleDetails']);
    const apolloClient = useApolloClient();

    const moduleId = module.id;

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('capModuleDetails:updateDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateCapModuleMutation, UpdateCapModuleMutationVariables>({
                    mutation: UpdateCapModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('capModuleDetails:updateDrawer.messages.updateSuccess'),
                key: 'primary',
            });

            onComplete();
        },
        [onComplete, apolloClient, moduleId, t]
    );

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['displayName'], module),
        }),
        [module]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateCapModule" layout="vertical" name="updateCapModule" onSubmitCapture={handleSubmit}>
                    <InputField
                        {...t('capModuleDetails:updateDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default CapModuleUpdateForm;
